import{runPrfCeremony,type StepUpProof}from"./webauthn";

const VAULT="/api/v1/security-console";

export const VAULT_ITEM_TYPES=[
  "LOGIN","SUBSCRIPTION","WIFI","SOFTWARE_LICENSE","SECURE_NOTE","OTHER"
]as const;
export type VaultItemType=typeof VAULT_ITEM_TYPES[number];

export type VaultItem={
  id:string;
  version:number;
  item_type:VaultItemType|string;
  status:string;
  pending_delete_at?:string|null;
  updated_at?:string|null;
  title?:string;
  url?:string;
  username?:string;
  password?:string;
  notes?:string;
  category?:string;
  tags?:string;
  price?:string;
  currency?:string;
  billing_cycle?:string;
  renewal_date?:string;
  trial_end?:string;
  cancellation_deadline?:string;
  cancellation_url?:string;
  payment_method_label?:string;
  license_key?:string;
  wifi_ssid?:string;
  totp_secret?:string;
  schema?:string;
};

export type VaultPayload={
  items:VaultItem[];
  security:Record<string,any>;
  subscriptions:Record<string,any>;
  recovery?:Record<string,any>|null;
  authenticated?:boolean;
};

export type VaultHistoryEntry={
  version:number;
  title?:string|null;
  item_type?:string|null;
  created_at:string;
  is_current:boolean;
};

export class VaultApiError extends Error{
  status:number;
  constructor(status:number,message:string){super(message);this.name="VaultApiError";this.status=status}
}

async function req<T>(path:string,init?:RequestInit):Promise<T>{
  const response=await fetch(`${VAULT}${path}`,{
    credentials:"include",
    headers:{Accept:"application/json","Content-Type":"application/json",...(init?.headers??{})},
    ...init,
  });
  const body=await response.json().catch(()=>({}));
  if(!response.ok){
    const detail=typeof body?.detail==="string"?body.detail:`vault_request_failed_${response.status}`;
    throw new VaultApiError(response.status,detail);
  }
  return body as T;
}

type OptionsResponse={ceremony_id:string;publicKey:unknown};

async function proof(optionsPath:string):Promise<StepUpProof>{
  const options=await req<OptionsResponse>(optionsPath,{method:"POST",body:"{}"});
  return runPrfCeremony(options.ceremony_id,options.publicKey);
}

async function freshStepUp():Promise<StepUpProof>{
  return proof("/unlock/options");
}

async function openWithExistingSession():Promise<VaultPayload>{
  const p=await freshStepUp();
  return req<VaultPayload>("/vault/list/verify",{method:"POST",body:JSON.stringify(p)});
}

async function openWithNewSession():Promise<VaultPayload>{
  const p=await proof("/open/options");
  return req<VaultPayload>("/open/verify",{method:"POST",body:JSON.stringify(p)});
}

export const vaultApi={
  async open():Promise<VaultPayload>{
    try{return await openWithExistingSession()}
    catch(error){
      if(error instanceof VaultApiError&&error.status===401)return openWithNewSession();
      throw error;
    }
  },

  async refresh():Promise<VaultPayload>{
    return openWithExistingSession();
  },

  async create(itemType:VaultItemType,payload:Record<string,unknown>):Promise<Record<string,any>>{
    const p=await freshStepUp();
    return req("/vault/create/verify",{
      method:"POST",
      body:JSON.stringify({...p,item_type:itemType,payload}),
    });
  },

  async update(itemId:string,itemType:VaultItemType,payload:Record<string,unknown>):Promise<Record<string,any>>{
    const p=await freshStepUp();
    return req(`/vault/${encodeURIComponent(itemId)}/update/verify`,{
      method:"POST",
      body:JSON.stringify({...p,item_type:itemType,payload}),
    });
  },

  async history(itemId:string):Promise<VaultHistoryEntry[]>{
    const p=await freshStepUp();
    const result=await req<{history:VaultHistoryEntry[]}>(`/vault/${encodeURIComponent(itemId)}/history/verify`,{
      method:"POST",body:JSON.stringify(p),
    });
    return result.history??[];
  },

  async scheduleDelete(itemId:string):Promise<{scheduled:boolean;delete_after:string;grace_days:number;recoverable_until_then:boolean}>{
    const p=await freshStepUp();
    return req(`/vault/${encodeURIComponent(itemId)}/delete/verify`,{
      method:"POST",body:JSON.stringify(p),
    });
  },

  async cancelDelete(itemId:string):Promise<{restored:boolean}>{
    const p=await freshStepUp();
    return req(`/vault/${encodeURIComponent(itemId)}/cancel-delete/verify`,{
      method:"POST",body:JSON.stringify(p),
    });
  },

  async restoreVersion(itemId:string,version:number):Promise<Record<string,any>>{
    const p=await freshStepUp();
    return req(`/vault/${encodeURIComponent(itemId)}/restore-version/verify`,{
      method:"POST",body:JSON.stringify({...p,version}),
    });
  },

  async generatePassword(length=32):Promise<string>{
    const result=await req<{password:string}>(`/password/generate?profile=strong&length=${encodeURIComponent(String(length))}`,{method:"GET"});
    return result.password;
  },
};
